// First start: no account exists yet. The person who installed the app creates the first
// administrator, confirmed with the one-time setup code that the server prints to its log and keeps
// in DATA_DIR/setup-code.txt (not APP_PASSWORD, which colleagues may know).
import { useEffect, useRef, useState } from 'react';
import { setup } from '../lib/api';
import { normalizeUsername, USERNAME_HINT, USERNAME_PATTERN } from '../lib/password';
import { accountErrorText, FormError, NewPasswordFields, newPasswordProblem } from '../components/AccountForms';
import { Icon } from '../components/Icon';

export function SetupPage() {
  const [setupCode, setSetupCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => firstRef.current?.focus(), []);

  const name = normalizeUsername(username);
  const usernameOk = USERNAME_PATTERN.test(name);
  const passwordProblem = newPasswordProblem(password, repeat, name);
  const problem = !setupCode.trim()
    ? 'Vpišite kodo za prvo nastavitev.'
    : !displayName.trim()
      ? 'Vpišite svoje ime in priimek.'
      : !usernameOk
        ? `Uporabniško ime ni veljavno. ${USERNAME_HINT}`
        : passwordProblem;

  const submit = async () => {
    setTried(true);
    if (problem || busy) return;
    setBusy(true);
    setError(null);
    try {
      await setup({ setupCode: setupCode.trim(), displayName, username: name, password });
      setSetupCode('');
      setPassword('');
      setRepeat('');
    } catch (err) {
      setError(accountErrorText(err, 'Nastavitev ni uspela.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page--center page--login">
      <form
        className="card login login--wide"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <span className="login__icon" aria-hidden="true">
          <Icon name="user" size={28} />
        </span>
        <h1 className="login__title">Prva nastavitev</h1>
        <p className="muted">
          Ustvarite skrbniški račun. Z njim boste dodajali sodelavce, vsak pa se bo prijavljal s svojim uporabniškim imenom in geslom.
        </p>
        <div className="field">
          <label htmlFor="setup-code">Koda za prvo nastavitev</label>
          <input
            ref={firstRef}
            id="setup-code"
            className="code-input"
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            maxLength={40}
            value={setupCode}
            onChange={(e) => setSetupCode(e.target.value)}
            aria-describedby="setup-code-hint"
            enterKeyHint="next"
          />
        </div>
        <p id="setup-code-hint" className="muted small">
          Strežnik je kodo izpisal v svoj dnevnik: v Synology Container Manager odprite Container → baam-transkripcija → Log in poiščite okvir
          »PRVA NASTAVITEV«. Ista koda je v datoteki setup-code.txt v podatkovni mapi aplikacije (npr. docker/baam-transkripcija/data). Velja le
          za to prvo nastavitev.
        </p>
        <div className="field">
          <label htmlFor="setup-name">Ime in priimek</label>
          <input
            id="setup-name"
            type="text"
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={100}
            enterKeyHint="next"
          />
        </div>
        <div className="field">
          <label htmlFor="setup-username">Uporabniško ime</label>
          <input
            id="setup-username"
            type="text"
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            aria-invalid={username !== '' && !usernameOk ? true : undefined}
            aria-describedby="setup-username-hint"
            maxLength={40}
            enterKeyHint="next"
          />
          <p id="setup-username-hint" className="muted small">
            {USERNAME_HINT}
          </p>
        </div>
        <NewPasswordFields idPrefix="setup" password={password} repeat={repeat} onPassword={setPassword} onRepeat={setRepeat} username={name} label="Geslo" />
        {error ? <FormError>{error}</FormError> : tried && problem ? <FormError>{problem}</FormError> : null}
        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? <Icon name="spinner" size={20} /> : <Icon name="check" size={20} />} Ustvari skrbniški račun
        </button>
      </form>
    </div>
  );
}
